import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Intent } from '@Components/designConstants';
import { NavMenuKey } from '@Components/NavBar/navbar-constants';
import { useCloseNav, useNavTopMenu } from '@Components/NavBar/useNavBarPopover';
import { useSetAreYouSure } from '../../redux/modals';
import { useNavBarState } from '../../redux/navbar';
import { useCompanyInfo } from '../hooks/useCompanyInfo';
import { BpmNavigateInput, LocationType } from './location-constants';

// Helper hook to navigate to a client route or a global route
export const useBpmNavigate = () => {
  const closeNav = useCloseNav();
  const navigate = useNavigate();
  const { setQueuedNavigation } = useNavBarState();
  const [, setShowClientSelect] = useNavTopMenu(NavMenuKey.CLIENT_SELECTOR);
  const setAreYouSure = useSetAreYouSure();
  const { cid } = useCompanyInfo();

  const bpmNavigate = useCallback(
    (input: BpmNavigateInput) => {
      const { type, prettyName, keepNavOpen = false, cleanup = () => {} } = input;
      // Some callers pass `location.fullPath` (leading slash) and others pass the already-stripped
      // key. Normalise here so we never build `//foo`, which react-router 7.18.3's URL validator
      // resolves as scheme-relative (`http://foo/`) and rejects as external navigation.
      const path = input.path.replace(/^\/+/, '');

      // Happy path #1: trying to nav to client, with a client selected
      if (type === LocationType.Client && cid) {
        navigate(`/${cid}/${path}`);
      }

      // Happy path #2: trying to nav to global, without a client selected
      if (type === LocationType.Global && !cid) {
        navigate(`/${path}`);
      }

      // Unhappy path #1: trying to nav to client, without a client selected
      // Open client selection popover, and then try to navigate after selection
      if (type === LocationType.Client && !cid) {
        setQueuedNavigation(input);
        setShowClientSelect(true);
        return;
      }

      // Unhappy path #2: trying to nav to global, with a client selected
      // Require confirmation dialog before leaving current client
      if (type === LocationType.Global && cid) {
        setAreYouSure({
          title: `Navigate away from ${cid}?`,
          message: `${prettyName} is a global page.`,
          okayText: 'Continue',
          intent: Intent.INFO,
          onOkay: () => {
            closeNav();
            navigate(`/${path}`);
            cleanup();
          }
        });
        return;
      }

      if (!keepNavOpen) {
        closeNav();
      }
    },
    [cid, closeNav, navigate, setAreYouSure, setQueuedNavigation, setShowClientSelect]
  );

  return bpmNavigate;
};
